import * as THREE from 'three';

export interface SurfaceTextureSet {
  map: THREE.CanvasTexture;
  roughnessMap: THREE.CanvasTexture;
  normalMap: THREE.CanvasTexture;
}

function seeded(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0xffffffff;
  };
}

function texture(canvas: HTMLCanvasElement, color = false): THREE.CanvasTexture {
  const result = new THREE.CanvasTexture(canvas);
  result.wrapS = THREE.RepeatWrapping;
  result.wrapT = THREE.RepeatWrapping;
  result.magFilter = THREE.LinearFilter;
  result.minFilter = THREE.LinearMipmapLinearFilter;
  result.colorSpace = color ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  return result;
}

function canvas(size: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const element = document.createElement('canvas');
  element.width = size;
  element.height = size;
  return [element, element.getContext('2d')!];
}

/** Compact, deterministic PBR maps for road and menu floor surfaces. */
export function asphaltSurface(size = 256): SurfaceTextureSet {
  const rand = seeded(0x51a7fa17);
  const [base, baseCtx] = canvas(size);
  const [rough, roughCtx] = canvas(size);
  const [normal, normalCtx] = canvas(size);

  baseCtx.fillStyle = '#1d222d';
  baseCtx.fillRect(0, 0, size, size);
  roughCtx.fillStyle = '#b7b7b7';
  roughCtx.fillRect(0, 0, size, size);
  normalCtx.fillStyle = '#8080ff';
  normalCtx.fillRect(0, 0, size, size);

  for (let i = 0; i < size * size * 0.32; i++) {
    const x = Math.floor(rand() * size);
    const y = Math.floor(rand() * size);
    const shade = 24 + Math.floor(rand() * 36);
    baseCtx.fillStyle = `rgb(${shade},${shade + 2},${shade + 7})`;
    baseCtx.fillRect(x, y, rand() > 0.82 ? 2 : 1, 1);
    const roughness = 125 + Math.floor(rand() * 105);
    roughCtx.fillStyle = `rgb(${roughness},${roughness},${roughness})`;
    roughCtx.fillRect(x, y, 1, 1);
    const nx = 119 + Math.floor(rand() * 19);
    const ny = 119 + Math.floor(rand() * 19);
    normalCtx.fillStyle = `rgb(${nx},${ny},255)`;
    normalCtx.fillRect(x, y, 1, 1);
  }

  // Long irregular wet patches break reflections instead of producing a
  // perfect mirror. They repeat far less visibly than circular puddles.
  for (let i = 0; i < 11; i++) {
    const x = rand() * size;
    const y = rand() * size;
    const w = 22 + rand() * 58;
    const h = 5 + rand() * 17;
    roughCtx.fillStyle = `rgba(35,35,35,${0.45 + rand() * 0.3})`;
    roughCtx.beginPath();
    roughCtx.ellipse(x, y, w, h, rand() * 0.35, 0, Math.PI * 2);
    roughCtx.fill();
    baseCtx.fillStyle = 'rgba(2,6,13,0.24)';
    baseCtx.beginPath();
    baseCtx.ellipse(x, y, w, h, rand() * 0.35, 0, Math.PI * 2);
    baseCtx.fill();
  }

  baseCtx.strokeStyle = 'rgba(0,0,0,0.44)';
  baseCtx.lineWidth = 1.1;
  roughCtx.strokeStyle = 'rgba(235,235,235,0.7)';
  for (let i = 0; i < 7; i++) {
    let x = rand() * size;
    let y = rand() * size;
    baseCtx.beginPath();
    roughCtx.beginPath();
    baseCtx.moveTo(x, y);
    roughCtx.moveTo(x, y);
    for (let p = 0; p < 5; p++) {
      x += (rand() - 0.5) * 22;
      y += 7 + rand() * 18;
      baseCtx.lineTo(x, y);
      roughCtx.lineTo(x, y);
    }
    baseCtx.stroke();
    roughCtx.stroke();
  }

  return { map: texture(base, true), roughnessMap: texture(rough), normalMap: texture(normal) };
}

export function concreteSurface(size = 256): SurfaceTextureSet {
  const rand = seeded(0xc011ce7e);
  const [base, baseCtx] = canvas(size);
  const [rough, roughCtx] = canvas(size);
  const [normal, normalCtx] = canvas(size);
  baseCtx.fillStyle = '#343741';
  baseCtx.fillRect(0, 0, size, size);
  roughCtx.fillStyle = '#dedede';
  roughCtx.fillRect(0, 0, size, size);
  normalCtx.fillStyle = '#8080ff';
  normalCtx.fillRect(0, 0, size, size);

  for (let i = 0; i < size * size * 0.18; i++) {
    const x = Math.floor(rand() * size);
    const y = Math.floor(rand() * size);
    const c = 42 + Math.floor(rand() * 34);
    baseCtx.fillStyle = `rgba(${c},${c + 1},${c + 6},${0.25 + rand() * 0.5})`;
    baseCtx.fillRect(x, y, rand() > 0.9 ? 2 : 1, rand() > 0.9 ? 2 : 1);
    const r = 185 + Math.floor(rand() * 65);
    roughCtx.fillStyle = `rgb(${r},${r},${r})`;
    roughCtx.fillRect(x, y, 1, 1);
    normalCtx.fillStyle = `rgb(${123 + Math.floor(rand() * 11)},${123 + Math.floor(rand() * 11)},255)`;
    normalCtx.fillRect(x, y, 1, 1);
  }

  baseCtx.strokeStyle = 'rgba(10,12,18,0.48)';
  baseCtx.lineWidth = 1.4;
  for (let i = 0; i < 5; i++) {
    baseCtx.beginPath();
    baseCtx.moveTo(rand() * size, rand() * size);
    baseCtx.lineTo(rand() * size, rand() * size);
    baseCtx.stroke();
  }

  return { map: texture(base, true), roughnessMap: texture(rough), normalMap: texture(normal) };
}
